import React, { useEffect, useState } from "react";
import Header from "units/dashboard/components/Header";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "../components/UserSidebar";
import { ProgressBar } from "react-loader-spinner";
import Footer from "units/dashboard/components/Footer";
import { useNavigate } from "react-router-dom";
import { getknowlageBaseInfo } from "../services/knowlageBaseInfo";
import { useSelector } from "react-redux";
import { showToast } from "toast/toastHelper";
import { Wallet, CreditCard, Minus, Plus, Info, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Helpers
 * ================================================================ */
const persianToEnglishDigits = (str) =>
  String(str).replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));

const englishToPersianDigits = (str) =>
  String(str).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

const formatNumberWithCommas = (num) =>
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// 👈 تابع واحد برای نمایش: amount/عدد همیشه داخلی لاتینه، این فقط لحظه‌ی نمایش تصمیم می‌گیره
const toDisplayDigits = (value, isEnglish) => {
  const latin = persianToEnglishDigits(String(value ?? ""));
  return isEnglish ? latin : englishToPersianDigits(latin);
};

/* ================================================================
 *  Main
 * ================================================================ */
const UserWalletInner = () => {
  const { t, i18n } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(0);
  // 👈 amount همیشه به‌صورت رشته‌ی لاتین با کاما ذخیره میشه (نه فارسی)
  const [amount, setAmount] = useState(formatNumberWithCommas(1000000));
  const [error, setError] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const navigate = useNavigate();
  const isEnglish = (i18n.language === "en");

  const options = [15000000, 10000000, 5000000];

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  useEffect(() => {
    const getUser = async () => {
      try {
        setLoading(true);
        const res = await getknowlageBaseInfo(accessToken);
        if (res?.ok) setWallet(res?.data?.wallet);
      } finally {
        setLoading(false);
      }
    };
    getUser();
  }, [accessToken]);

  const handleSelect = (value) => {
    setAmount(formatNumberWithCommas(value)); // 👈 لاتین خالص
    setError("");
  };

  const handleIncrease = () => {
    const cleanAmount = parseInt(
      persianToEnglishDigits(amount.toString().replace(/,/g, "")),
      10
    );
    const increased = (isNaN(cleanAmount) ? 0 : cleanAmount) + 1000000;
    setAmount(formatNumberWithCommas(increased)); // 👈 لاتین خالص
    setError("");
  };

  const handleDecrease = () => {
    const cleanAmount = parseInt(
      persianToEnglishDigits(amount.toString().replace(/,/g, "")),
      10
    );
    if (cleanAmount > 0) {
      const decreased = Math.max(cleanAmount - 1000000, 0);
      setAmount(formatNumberWithCommas(decreased)); // 👈 لاتین خالص
      setError("");
    }
  };

  const handleInputChange = (e) => {
    const rawValue = e.target.value;
    const english = persianToEnglishDigits(rawValue.replace(/,/g, ""));

    if (rawValue === "") {
      setAmount("");
      setError("");
      return;
    }

    const num = parseInt(english, 10);
    if (isNaN(num)) {
      setAmount("");
      setError(t("userWallet.errors.invalidNumber"));
      return;
    }

    if (num < 1000000) {
      setError(t("userWallet.errors.minAmount"));
    } else {
      setError("");
    }

    setAmount(formatNumberWithCommas(num)); // 👈 لاتین خالص، دیگه englishToPersianDigits نیست
  };

  const payButtonHandler = () => {
    const cleanAmount = persianToEnglishDigits(amount.toString().replace(/,/g, ""));
    const numericAmount = parseInt(cleanAmount, 10);

    if (isNaN(numericAmount) || numericAmount < 1000000) {
      showToast(t("userWallet.errors.minChargeAmount"), "error");
      return;
    }

    navigate("/payment-gateway-method", { state: { finalPrice: numericAmount } });
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl">
          <Header />
        </nav>
        <div className="flex-1 flex items-center justify-center">
          <ProgressBar visible height="80" width="80" barColor="#14B8A6" borderColor="#FCD34D" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            aria-hidden="true"
          />
        )}

        <aside
          className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:from-slate-900/95 dark:via-slate-900/98 dark:to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="px-3 sm:px-4 md:px-6 py-5 sm:py-8">

              {/* ================= Page Header ================= */}
              <div className="max-w-4xl mx-auto mb-5">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <Wallet size={18} className="text-amber-500" />
                  </span>
                  <div>
                    <h1 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-amber-100">
                      {t("userWallet.pageTitle")}
                    </h1>
                    <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t("userWallet.pageSubtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* ================= Main Grid ================= */}
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">

                  {/* ============ RIGHT COLUMN: موجودی + پیشنهادی + دلخواه ============ */}
                  <div className="space-y-4 order-1">

                    {/* ---- موجودی ---- */}
                    <div
                      className="
                        relative overflow-hidden rounded-2xl
                        bg-gradient-to-l from-teal-700 via-teal-600 to-teal-700
                        dark:from-[#0B2E2F] dark:via-[#0F3B3C] dark:to-[#0B2E2F]
                        border border-teal-500/30 dark:border-teal-400/20
                        shadow-[0_15px_40px_-15px_rgba(15,118,110,0.7)]
                        p-4
                      "
                    >
                      <div className="pointer-events-none absolute inset-0">
                        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />
                        <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-amber-300/20 blur-3xl" />
                      </div>

                      <div className="relative">
                        <p className="text-[11px] text-teal-100/80">
                          {t("userWallet.currentBalance")}
                        </p>
                        <p className={`mt-1.5 text-2xl font-extrabold text-white tabular-nums drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)] ${isEnglish && 'font-sans'}`}>
                          {toDisplayDigits(wallet.toLocaleString('en-US'), isEnglish)}{" "}
                          <span className="text-xs font-medium text-amber-200">
                            {t("userWallet.currency")}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* ---- مبالغ پیشنهادی ---- */}
                    <div>
                      <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-2 tracking-wide">
                        {t("userWallet.suggestedAmounts")}
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {options.map((opt) => {
                          const selected =
                            amount === formatNumberWithCommas(opt); // 👈 مقایسه‌ی لاتین با لاتین
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => handleSelect(opt)}
                              className={`
                                group relative overflow-hidden
                                flex flex-col items-center justify-center
                                py-2.5 rounded-xl
                                text-[11px] sm:text-xs font-bold
                                transition-all duration-200
                                ${selected
                                  ? "bg-gradient-to-br from-amber-300 to-amber-500 text-slate-900 border border-amber-400 shadow-[0_6px_18px_-6px_rgba(251,191,36,0.8)]"
                                  : "bg-white/60 dark:bg-white/5 text-slate-700 dark:text-slate-200 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10"
                                }
                              `}
                            >
                              <span className={`relative tabular-nums ${isEnglish && 'font-sans'}`}>
                                {toDisplayDigits((opt / 1000000).toLocaleString('en-US'), isEnglish)}
                              </span>
                              <span className="relative text-[9px] opacity-70 mt-0.5">
                                {t("userWallet.millionRial")}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ---- مبلغ دلخواه ---- */}
                    <div>
                      <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-2 tracking-wide">
                        {t("userWallet.customAmount")}
                      </p>
                      <div
                        className={`
                          flex items-center gap-1
                          rounded-xl p-1.5
                          bg-white/60 dark:bg-slate-800/60
                          border transition-all duration-200
                          ${isFocused
                            ? "border-amber-300/60 ring-2 ring-amber-300/30"
                            : "border-teal-500/20 dark:border-teal-400/20"
                          }
                        `}
                      >
                        <button
                          type="button"
                          onClick={handleDecrease}
                          disabled={
                            parseInt(
                              persianToEnglishDigits(amount.toString().replace(/,/g, "")),
                              10
                            ) <= 0 || amount === ""
                          }
                          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                          aria-label={t("userWallet.ariaDecrease")}
                        >
                          <Minus size={14} />
                        </button>

                        <div className="relative flex-1 min-w-0">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={toDisplayDigits(amount, isEnglish)} // 👈 نمایش بر اساس زبان
                            onChange={handleInputChange}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            className={`w-full bg-transparent text-center text-sm sm:text-base font-extrabold text-slate-800 dark:text-amber-100 tabular-nums outline-none py-1 ${isEnglish && 'font-sans'}`}
                            aria-label={t("userWallet.ariaAmount")}
                          />
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 dark:text-slate-500 pointer-events-none">
                            {t("userWallet.currency")}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={handleIncrease}
                          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 hover:scale-105 active:scale-95"
                          aria-label={t("userWallet.ariaIncrease")}
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      {/* Error / Hint */}
                      <div className="mt-2 h-5 flex items-center gap-1.5">
                        {error ? (
                          <>
                            <span className="h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
                            <p className="text-[11px] font-bold text-rose-600 dark:text-rose-300">
                              {error}
                            </p>
                          </>
                        ) : (
                          <>
                            <Info size={11} className="text-teal-600/70 dark:text-teal-300/70 shrink-0" />
                            <p className={`text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 ${isEnglish && 'font-sans'}`}>
                              {t("userWallet.minAmountHint")}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ============ LEFT COLUMN: خلاصه تراکنش ============ */}
                  <div className="order-2">
                    <div className="rounded-2xl bg-white/60 dark:bg-white/5 border border-teal-500/15 dark:border-teal-400/15 backdrop-blur-md overflow-hidden">
                      <div className="px-4 py-3 border-b border-teal-500/10 dark:border-teal-400/10">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200">
                          {t("userWallet.transactionSummary")}
                        </p>
                      </div>

                      <dl className="divide-y divide-teal-500/10 dark:divide-teal-400/10 text-xs">
                        <div className="flex items-center justify-between px-4 py-3">
                          <dt className="text-slate-500 dark:text-slate-400">
                            {t("userWallet.chargeableAmount")}
                          </dt>
                          <dd className={`font-extrabold text-slate-800 dark:text-amber-100 tabular-nums ${isEnglish && 'font-sans'}`}>
                            {toDisplayDigits(amount, isEnglish) || toDisplayDigits(0, isEnglish)} {t("userWallet.currency")}
                          </dd>
                        </div>

                        <div className="flex items-center justify-between px-4 py-3">
                          <dt className="text-slate-500 dark:text-slate-400">
                            {t("userWallet.fee")}
                          </dt>
                          <dd className="font-bold text-emerald-600 dark:text-emerald-400">
                            {t("userWallet.free")}
                          </dd>
                        </div>

                        <div className="flex items-center justify-between px-4 py-3">
                          <dt className="text-slate-500 dark:text-slate-400">
                            {t("userWallet.paymentGateway")}
                          </dt>
                          <dd className="font-bold text-slate-700 dark:text-slate-200">
                            {t("userWallet.bankGateway")}
                          </dd>
                        </div>

                        <div className="flex items-center justify-between px-4 py-3 bg-teal-500/5 dark:bg-teal-400/5">
                          <dt className="text-slate-700 dark:text-amber-100 font-bold">
                            {t("userWallet.finalAmount")}
                          </dt>
                          <dd className={`font-extrabold text-amber-600 dark:text-amber-300 tabular-nums ${isEnglish && 'font-sans'}`}>
                            {toDisplayDigits(amount, isEnglish) || toDisplayDigits(0, isEnglish)} {t("userWallet.currency")}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>

                {/* ============ Pay Button ============ */}
                <button
                  type="button"
                  onClick={payButtonHandler}
                  className="
                    group relative overflow-hidden
                    mt-5 w-full
                    flex items-center justify-center gap-2
                    py-3.5 px-4 rounded-xl
                    text-sm font-bold text-white
                    bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                    shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                    hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                    hover:shadow-[0_12px_32px_-8px_rgba(20,184,166,1)]
                    hover:scale-[1.005] active:scale-[0.995]
                    transition-all duration-300
                  "
                >
                  <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  <CreditCard size={15} className="relative" />
                  <span className="relative">{t("userWallet.continuePayment")}</span>
                </button>

                {/* Trust line */}
                <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                  <ShieldCheck size={11} className="text-emerald-500/70" />
                  {t("userWallet.securePayment")}
                </div>
              </div>

              <div className="max-w-4xl mx-auto mt-8">
                <Footer />
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
};

const UserWallet = () => (
  <SidebarProvider>
    <UserWalletInner />
  </SidebarProvider>
);

export default UserWallet;